// src/pages/ContactSupport.tsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import HighLogo from "../../HighLogo";
import logo from "../../../assets/logo.png";
import { useAuth } from "../../../shared/contexts/AuthContext";
import api from "../../../shared/api/axios";
import LanguageSelector from "../../components/LanguageSelector";
import { useTranslation } from "react-i18next";

const SUPPORT_EMAIL = process.env.REACT_APP_SUPPORT_EMAIL || "support@example.com";


// flip to true once you wire a backend endpoint (kept from your old version)
const ENABLE_SUPPORT_API = false;

const ContactSupport: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth() as any;
  const { t } = useTranslation();
  const [showLanguage, setShowLanguage] = useState(false);
  // lock page scroll like your Help pages (only inner panel scrolls)
  useEffect(() => {
    const prevHtmlOverflow = document.documentElement.style.overflow;
    const prevBodyOverflow = document.body.style.overflow;

    document.documentElement.style.overflow = "hidden";
    document.body.style.overflow = "hidden";

    return () => {
      document.documentElement.style.overflow = prevHtmlOverflow;
      document.body.style.overflow = prevBodyOverflow;
    };
  }, []);

  const me = useMemo(
    () => ({
      id: user?._id || user?.id || "",
      profilename:
        user?.profilename ||
        user?.displayName ||
        user?.username ||
        user?.name ||
        "",
      email: user?.email || "",
    }),
    [user]
  );

  // contact form
  const [fromName, setFromName] = useState("");
  const [fromEmail, setFromEmail] = useState("");
  const [category, setCategory] = useState("general");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [includeDiagnostics, setIncludeDiagnostics] = useState(true);
  const [sending, setSending] = useState(false);

  // Prefill when token/user is active (but allow editing)
  useEffect(() => {
    setFromName((prev) => (prev?.trim() ? prev : me.profilename || ""));
    setFromEmail((prev) => (prev?.trim() ? prev : me.email || ""));
  }, [me.profilename, me.email]);

  const diagnostics = useMemo(() => {
    const ua = navigator.userAgent;
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "unknown";
    const now = new Date().toISOString();
    const uid = me.id || "(not logged in)";
    return `\n\n---\nDiagnostics\nuserId: ${uid}\ntime: ${now}\nuserAgent: ${ua}\ntimezone: ${tz}\n---`;
  }, [me.id]);

  const bodyText = useMemo(() => {
    const lines = [
      `Name: ${fromName || "(not provided)"}`,
      `Email: ${fromEmail || "(not provided)"}`,
      `Category: ${category}`,
      "",
      message || "(no message provided)",
      includeDiagnostics ? diagnostics : "",
    ];
    return lines.join("\n");
  }, [fromName, fromEmail, category, message, includeDiagnostics, diagnostics]);

  const validEmail = /\S+@\S+\.\S+/.test(fromEmail || "");
  const nameOk = (fromName || "").trim().length > 1;
  const subjectOk = subject.trim().length > 0;
  const messageOk = message.trim().length > 0;
  const canSend = nameOk && validEmail && subjectOk && messageOk && !sending;

  const submitViaEmail = () => {
    const href = `mailto:${encodeURIComponent(
      SUPPORT_EMAIL
    )}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(
      bodyText
    )}`;
    window.location.href = href;
  };

  const submitViaApi = async () => {
    try {
      setSending(true);
      await api.post("/support/contact", {
        name: fromName,
        email: fromEmail,
        category,
        subject,
        message,
        diagnostics: includeDiagnostics ? diagnostics : undefined,
        userId: me.id || undefined,
      });
      alert("✅ Thanks! Your message was sent.");
      setSubject("");
      setMessage("");
    } catch (e: any) {
      console.error(e);
      alert(e?.response?.data?.msg || "❌ Could not submit your request.");
    } finally {
      setSending(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSend) return;

    if (ENABLE_SUPPORT_API) {
      await submitViaApi();
    } else {
      submitViaEmail();
    }
  };

  return (
    <div style={shell}>
      {/* Top bar */}
      <header style={header}>
        <div style={headerLeft} onClick={() => navigate("/gallery")}>
  <HighLogo
    src={logo}
    style={{ width: 170, display: "block" }}
    padding={0}
  />
</div>

        <div style={{ display: "flex", gap: 10, alignItems: "center", marginRight: 10 }}>
  <button
    type="button"
    onClick={() => navigate(-1)}
    style={ghostBtn}
    aria-label={t("platformGuide.meta.back", "Back")}
    title={t("platformGuide.meta.back", "Back")}
  >
    ← {t("platformGuide.meta.back", "Back")}
  </button>

  <button
    type="button"
    onClick={() => setShowLanguage(true)}
    style={ghostBtn}
  >
    🌐 {t("home.footer.language", "Language")}
  </button>
</div>
      </header>

      <main style={main}>
        {/* Intro */}
        <section style={introCard}>
          <div style={introTop}>
            <div>
              <h1 style={h1}>Contact Support</h1>
              <p style={subhead}>
                Tell us what happened and we’ll help you fix it. Name and email
                are required (prefilled when you’re logged in).
              </p>
            </div>

            <div style={pillRow}>
              <span style={pill}>
                <span style={pillDot} />
                {me.id ? "SIGNED IN" : "GUEST"}
              </span>
            </div>
          </div>
        </section>

        {/* Form card */}
        <section style={formCard}>
          <form onSubmit={handleSubmit} style={{ width: "100%" }}>
            <div style={grid}>
              <div style={row2}>
                <div>
                  <label style={label}>Name *</label>
                  <input
                    style={input}
                    type="text"
                    value={fromName}
                    onChange={(e) => setFromName(e.target.value)}
                    placeholder="Your name"
                    required
                  />
                  {!nameOk && fromName && (
                    <div style={errorText}>Please enter your name.</div>
                  )}
                </div>

                <div>
                  <label style={label}>Email *</label>
                  <input
                    style={input}
                    type="email"
                    value={fromEmail}
                    onChange={(e) => setFromEmail(e.target.value)}
                    placeholder="you@example.com"
                    required
                  />
                  {!validEmail && fromEmail && (
                    <div style={errorText}>Enter a valid email address.</div>
                  )}
                </div>
              </div>

              <div>
                <label style={label}>Category</label>
                <select
                  style={select}
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                >
                  <option value="general">General</option>
                  <option value="account">Account / Login</option>
                  <option value="privacy">Privacy &amp; Safety</option>
                  <option value="bug">Bug Report</option>
                  <option value="feature">Feature Request</option>
                  <option value="billing">Billing</option>
                </select>
              </div>

              <div>
                <label style={label}>Subject *</label>
                <input
                  style={input}
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Short summary"
                  required
                />
              </div>

              <div>
                <label style={label}>Message *</label>
                <textarea
                  style={textarea}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Explain what happened, how to reproduce it, and what you expected."
                  required
                />
              </div>

              <label style={checkboxRow}>
                <input
                  type="checkbox"
                  checked={includeDiagnostics}
                  onChange={(e) => setIncludeDiagnostics(e.target.checked)}
                />
                <span>
                  Include diagnostics (userId, device, time)
                  <span style={{ display: "block", ...muted, marginTop: 4 }}>
                    Helps us troubleshoot faster. No message content is auto-read
                    from your device.
                  </span>
                </span>
              </label>

              <div style={actions}>
                <button type="submit" style={ctaBtn} disabled={!canSend}>
                  {sending
                    ? "Sending…"
                    : ENABLE_SUPPORT_API
                    ? "Send to Support"
                    : "Send via Email"}
                </button>

                <button
                  type="button"
                  style={ghostBtn2}
                  onClick={() => {
                    setSubject("");
                    setMessage("");
                  }}
                >
                  Clear
                </button>

                <button
                  type="button"
                  style={ghostBtn2}
                  onClick={() => navigate("/help")}
                >
                  Back to Help
                </button>
              </div>

              <div style={footNote}>
                We’ll reply to <b>{fromEmail || "your email"}</b>. Make sure it’s
                correct.
              </div>
            </div>
          </form>
        </section>
      </main>

      {showLanguage && (
  <div style={languageOverlay} onClick={() => setShowLanguage(false)}>
    <div
      onClick={(e) => e.stopPropagation()}
      style={languageModalWrap}
    >
      <LanguageSelector
        onClose={() => setShowLanguage(false)}
        offsetY={0}
      />
    </div>
  </div>
)}

      <style>{`
        input, textarea, select { transition: border-color 140ms ease, box-shadow 140ms ease; }
        input:focus, textarea:focus, select:focus { 
          border-color: rgba(163,230,53,0.55);
          box-shadow: 0 0 0 3px rgba(163,230,53,0.12);
        }
      `}</style>
    </div>
  );
};

export default ContactSupport;

/* ----------------- styles (same family as HelpSupportStart + Guides) ----------------- */

const shell: React.CSSProperties = {
  height: "100vh",
  width: "100%",
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
  background:
    "radial-gradient(circle at top, #020617 0, #000 40%, #020617 100%)",
  color: "#f9fafb",
  fontFamily: 'system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif',
};

const header: React.CSSProperties = {
  position: "sticky",
  top: 0,
  zIndex: 30,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "10px 25px",
  background: "rgba(2, 6, 23, 0.9)",
  backdropFilter: "blur(10px)",
  borderBottom: "1px solid rgba(148,163,184,0.2)",
};

const headerLeft: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  cursor: "pointer",
  marginLeft: "-28px",
  marginTop: '5px',
}; 

const ghostBtn: React.CSSProperties = {
  padding: "8px 14px",
  borderRadius: 999,
  border: "1px solid #4b5563",
  background: "transparent",
  color: "#e5e7eb",
  cursor: "pointer",
  fontSize: 13,
  fontWeight: 700,
};

const main: React.CSSProperties = {
  flex: 1,
  display: "grid",
  gridTemplateRows: "auto minmax(0, 1fr)",
  gap: 14,
  padding: "18px 7vw 18px",
  overflow: "hidden",
  boxSizing: "border-box",
};

const introCard: React.CSSProperties = {
  borderRadius: 22,
  border: "1px solid rgba(148,163,184,0.22)",
  background: "linear-gradient(145deg, rgba(15,23,42,0.85), rgba(3,7,18,1))",
  boxShadow: "0 18px 45px rgba(0,0,0,0.75)",
  padding: "14px 16px",
  overflow: "hidden",
};

const introTop: React.CSSProperties = {
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: 14,
};

const h1: React.CSSProperties = {
  margin: 0,
  fontSize: 22,
  letterSpacing: -0.03,
  lineHeight: 1.1,
};

const subhead: React.CSSProperties = {
  margin: "10px 0 0",
  color: "#9ca3af",
  fontSize: 14.5,
  lineHeight: 1.55,
  maxWidth: 920,
};

const pillRow: React.CSSProperties = {
  flex: "0 0 auto",
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
};

const pill: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  borderRadius: 999,
  border: "1px solid rgba(148,163,184,0.25)",
  background: "rgba(255,255,255,0.06)",
  padding: "8px 12px",
  fontSize: 11,
  color: "#cbd5e1",
  letterSpacing: 0.9,
  fontWeight: 900,
  whiteSpace: "nowrap",
};

const pillDot: React.CSSProperties = {
  width: 8,
  height: 8,
  borderRadius: 999,
  background: "#a3e635",
  boxShadow: "0 0 0 3px rgba(255,255,255,0.06)",
};

const formCard: React.CSSProperties = {
  borderRadius: 22,
  border: "1px solid rgba(148,163,184,0.22)",
  background: "linear-gradient(145deg, rgba(15,23,42,0.90), rgba(3,7,18,1))",
  boxShadow: "0 22px 55px rgba(0,0,0,0.8)",
  overflow: "hidden",
  padding: 16,
};

const grid: React.CSSProperties = {
  display: "grid",
  gap: 12,
};

const row2: React.CSSProperties = {
  display: "grid",
  gap: 12,
  gridTemplateColumns: "1fr 1fr",
};

const label: React.CSSProperties = {
  display: "block",
  fontSize: 12.5,
  fontWeight: 800,
  color: "#cbd5e1",
  marginBottom: 6,
};

const input: React.CSSProperties = {
  width: "100%",
  padding: "11px 12px",
  borderRadius: 12,
  border: "1px solid rgba(148,163,184,0.35)",
  fontSize: 14,
  outline: "none",
  background: "rgba(2,6,23,0.7)",
  color: "#e5e7eb",
  boxSizing: "border-box",
};

const select: React.CSSProperties = {
  ...input,
  appearance: "auto",
};

const textarea: React.CSSProperties = {
  ...input,
  minHeight: 140,
  resize: "vertical",
};

const checkboxRow: React.CSSProperties = {
  display: "flex",
  gap: 10,
  alignItems: "flex-start",
  fontSize: 13.5,
  color: "#e5e7eb",
  lineHeight: 1.35,
  userSelect: "none",
};

const muted: React.CSSProperties = {
  color: "#9ca3af",
  fontSize: 12,
};

const actions: React.CSSProperties = {
  display: "flex",
  gap: 10,
  flexWrap: "wrap",
  alignItems: "center",
  marginTop: 4,
};

const ctaBtn: React.CSSProperties = {
  padding: "10px 16px",
  borderRadius: 999,
  border: "none",
  background: "linear-gradient(135deg,#22c55e,#a3e635)",
  color: "#020617",
  cursor: "pointer",
  fontSize: 13,
  fontWeight: 900,
  whiteSpace: "nowrap",
  opacity: 1,
};

const ghostBtn2: React.CSSProperties = {
  padding: "10px 16px",
  borderRadius: 999,
  border: "1px solid rgba(148,163,184,0.35)",
  background: "rgba(2, 6, 23, 0.35)",
  color: "#e5e7eb",
  cursor: "pointer",
  fontSize: 13,
  fontWeight: 800,
  whiteSpace: "nowrap",
};

const errorText: React.CSSProperties = {
  color: "#fca5a5",
  fontSize: 12,
  marginTop: 6,
};

const footNote: React.CSSProperties = {
  marginTop: 6,
  color: "#9ca3af",
  fontSize: 12.5,
};

const languageOverlay: React.CSSProperties = {
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 999999,
  backgroundColor: "rgba(0,0,0,0.78)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const languageModalWrap: React.CSSProperties = {
  transform: "translateY(5px)",
};